import React, { useContext, useEffect, useState } from "react";

import {
  ActionButton,
  IIconProps,
  IObjectWithKey,
  SelectionMode,
  Selection,
  Spinner,
  SpinnerSize,
  Stack,
  SearchBox,
  styled,
  Separator,
  TooltipHost,
  ITooltipProps,
  TooltipDelay,
  DirectionalHint,
  ITooltipHostStyles,
  ISearchBoxStyles,
  Icon,
} from "@fluentui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { ActivityTimingTracker } from "../../Utils/ActivityTimingTracker";
import { RequestCustomerEditAccount as CustomerInfo } from "./Customer.types";
import {
  DefaultCurrentPage,
  DefaultCurrentPageSize,
  DefaultTotalPages,
  ErrorsDefaultState,
  InitialQuotationInfo,
  NEW_ACTION,
} from "../Quotation/Common/QuotationData";
import { CustomerList } from "./CustomerList";
import { useLocalStorageContext } from "../../Provider/LocalStorageProvider";
import { QuotationContext } from "../Quotation/QuotationContext";
import { IQuotationType, QuotationInfoType } from "../Quotation/Quotation.types";
import {
  searchBoxIconWidthStyles,
  searchBoxWidthStyles,
  searchIconStyle,
} from "./CustomerStyle";

interface IAccountsProps {
  region: string;
  searchRegion: string[];
}

interface CustomerLocationState {
  isSubmitClick: boolean;
}

export const Customer: React.FunctionComponent<IAccountsProps> = (props) => {
  let selectedValue: CustomerInfo;
  const AddIcon: IIconProps = { iconName: "Add" };
  const EditIcon: IIconProps = { iconName: "Edit" };
  const DeleteIcon: IIconProps = { iconName: "Delete" };
  const navigate = useNavigate();
  const { genericRole } = useLocalStorageContext();

  const [selectedGridData, setSelectedGridData] = useState<{
    items: any[];
  }>({ items: [] });
  const [currentPage, setCurrentPage] = useState<number>(DefaultCurrentPage);
  const [currentPageSize, setCurrentPageSize] = useState<number>(
    DefaultCurrentPageSize
  );
  const [isNewQuoteButtonDisabled, setIsNewQuoteButtonDisabled] =
    useState(true);
  const isDisabled = Boolean(
    !(selectedGridData?.items.length && selectedGridData?.items.length > 0)
  );
  const { setQuotationInfo } = useContext(
    QuotationContext
  ) as QuotationInfoType;
  const { setQuotationError } = useContext(
    QuotationContext
  ) as IQuotationType;
  const [searchTextResult, setSearchTextResult] = useState("NA");
  const [searchTextResultTemp, setSearchTextResultTemp] = useState("NA");
  const [isClearClick, setIsClearClick] = useState(false);
  const [searchResultCount, setSearchResultCount] = useState<number>(0);
  const [searchResponseMessage, setSearchResponseMessage] =
    useState<string>("");
  useEffect(() => {
    if (searchTextResult != "NA") {
      searchResultCount === 0
        ? setSearchResponseMessage("No result found in search result.")
        : setSearchResponseMessage(
            `Record updated. ${searchResultCount} record found in search result.`
          );
    }
  }, [searchResultCount]);

  const [isAddButtonDisabled, setIsAddButtonDisabled] = useState(false);

  if (selectedGridData) {
    selectedValue = selectedGridData.items[0] as CustomerInfo;
  }

  const useScreenScale = () => {
    const [screenScale, setScreenScale] = useState(window.devicePixelRatio);

    useEffect(() => {
      const handleResize = () => {
        setScreenScale(window.devicePixelRatio);
      };

      // Attach the event listener to the window resize event
      window.addEventListener("resize", handleResize);

      // Clean up the event listener on component unmount
      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }, []); // Empty dependency array ensures that the effect runs only once after mount

    return screenScale;
  };

  const screenScale = useScreenScale();

  useEffect(() => {
    if (
      selectedValue?.discount?.afilliationSelected.length > 0 &&
      selectedValue?.accountId.length > 0
    ) {
      setIsNewQuoteButtonDisabled(false);
    } else {
      setIsNewQuoteButtonDisabled(true);
    }
    if (selectedValue?.accountId.length > 0) {
      setIsAddButtonDisabled(true);
    } else {
      setIsAddButtonDisabled(false);
    }
  }, [selectedGridData.items[0]]);

  const selection = React.useMemo(
    () =>
      new Selection({
        onSelectionChanged: () => {
          const selectedCustomer = selection.getSelection();
          if (selectedCustomer?.length) {
            setSelectedGridData({ items: selectedCustomer });
          } else {
            setSelectedGridData({ items: [] });
          }
        },
      }),

    [selectedGridData.items[0]]
  );

  const handleQuoteClick = () => {
    setQuotationError({
      ...ErrorsDefaultState,
    });
    setQuotationInfo({
      ...InitialQuotationInfo,
      quoteLineItems: [],
      quoteLineItemsClone: [],
      standaloneItems: [],
      standaloneItemsClone: [],
      bundleItems: [],
      bundleItemsClone: [],
    });
    navigate("/AddQuotation", {
      state: {
        ...selectedValue,
        actionName: NEW_ACTION,
        customerId: selectedValue.accountId,
      },
    });
  };

  const _OnClickEditAccount = () => {
    navigate("/CustomerEditContact", { state: selectedValue });
  };

  const onClickAddAccount = () => {
    console.log('🎯 User clicked "Add New Account"');
    
    navigate("/CustomerNewAccount");
  };

  const hostStyles: Partial<ITooltipHostStyles> = {
    root: { display: "inline-block" },
  };

  // const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  // useEffect(() => {
  //   setWindowWidth(window.innerWidth);
  // }, [window.innerWidth]);

  return (
    <Stack style={{ height: "75vh",paddingLeft:'25px',paddingRight:'70px', overflowY: "hidden" }}>
      <Stack
        horizontal
        horizontalAlign="space-between"
        style={{ msOverflowX: "hidden",marginLeft: "40px", minHeight: "50px", minWidth: "320px" }}
      >
        <Stack horizontal>
          <ActionButton
            disabled={genericRole === -1 || isAddButtonDisabled}
            onClick={onClickAddAccount}
            iconProps={AddIcon}
          >
            Add New Account
          </ActionButton>
          <ActionButton
            onClick={handleQuoteClick}
            disabled={genericRole === -1 || isNewQuoteButtonDisabled}
            iconProps={AddIcon}
          >
            Add New Quotation
          </ActionButton>
        </Stack>
        <Stack horizontal>
          <TooltipHost
            content={
              window.innerWidth < 500 ? "Search by Customer Name or Customer Account or Email " : ""
            }
            delay={TooltipDelay.zero}
            directionalHint={DirectionalHint.bottomCenter}
            styles={hostStyles}
          >
            <SearchBox
              styles={
                window.innerWidth < 500
                  ? searchBoxIconWidthStyles
                  : searchBoxWidthStyles
              }
              placeholder={
                window.innerWidth > 500 ? "Search by Customer Name or Customer Account or Email ": ""
              }
              aria-label={searchResponseMessage}
              onClear={() => {
                setIsClearClick(true);
                setSearchTextResult("NA");
              }}
              onChange={(e) => {
                if (e?.target?.value === "" && !isClearClick) {
                  setSearchTextResult("NA");
                }
                else
                {
                  setSearchTextResultTemp(e?.target?.value ?? "");
                }
                setIsClearClick(false);
              }}
              onSearch={(newValue) => {
                if (newValue.length > 0) {
                  setSearchTextResult(newValue);
                } else {
                  setSearchTextResult("NA");
                }
              }}
            />
          </TooltipHost>
          <Stack
            role="button"
            tabIndex={0}
            aria-label="Search"
            onClick={() => {
              setSearchTextResult(searchTextResultTemp);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                setSearchTextResult(searchTextResultTemp);
              }
            }}
            onKeyUp={(e) => {
              if (e.key === " ") {
                e.preventDefault();
                setSearchTextResult(searchTextResultTemp);
              }
            }}
          >
            <Icon
              iconName="Search"
              style={searchIconStyle}
            />
          </Stack>

          <Stack
            aria-live="polite"
            style={{ position: "absolute", left: "-9999px", opacity: "0" }}
          >
            {searchResponseMessage}
          </Stack>
        </Stack>
      </Stack>
      <CustomerList
        region={props.region}
        searchText={searchTextResult}
        setSearchResultCount={setSearchResultCount}
        searchRegion={props.searchRegion}
        selection={selection}
        selectedGridData={selectedGridData}
        setIsAddButtonDisabled={setIsAddButtonDisabled}
      />
    </Stack>
  );
};
